import React, { useEffect, useRef, useState } from 'react';
import type { ConfigFieldDiff, FieldDecision } from '../../utils/nltConfigFieldDiff';
import NltTextFieldDiffLines from '../NltTextFieldDiffLines';
import './AcFieldPatchDiff.css';

export interface AcFieldPatchDiffProps {
  diff?: ConfigFieldDiff;
  decision?: FieldDecision;
  onDecision?: (path: string, decision: FieldDecision) => void;
  children: React.ReactNode;
}

const AcFieldPatchDiff: React.FC<AcFieldPatchDiffProps> = ({
  diff,
  decision = 'pending',
  onDecision,
  children,
}) => {
  const prevDecisionRef = useRef<FieldDecision>(decision);
  const [flashClass, setFlashClass] = useState('');

  useEffect(() => {
    const prev = prevDecisionRef.current;
    prevDecisionRef.current = decision;
    if (prev === decision) return;
    if (decision === 'accepted') {
      setFlashClass('ac-field-patch--flash-accept');
      const t = setTimeout(() => setFlashClass(''), 700);
      return () => clearTimeout(t);
    }
    if (decision === 'rejected') {
      setFlashClass('ac-field-patch--flash-reject');
      const t = setTimeout(() => setFlashClass(''), 700);
      return () => clearTimeout(t);
    }
  }, [decision]);

  if (!diff) {
    return <>{children}</>;
  }

  const accepted = decision === 'accepted';
  const rejected = decision === 'rejected';
  const pending = decision === 'pending';

  return (
    <div
      className={[
        'ac-field-patch',
        accepted ? 'ac-field-patch--accepted' : '',
        rejected ? 'ac-field-patch--rejected' : '',
        flashClass,
      ]
        .filter(Boolean)
        .join(' ')}
      data-nlt-patch-path={diff.path}
    >
      {/* Bar: label + status/actions */}
      <div className="ac-field-patch__bar">
        <span className="ac-field-patch__label">{diff.label}</span>

        {pending && onDecision && (
          <div className="ac-field-patch__actions">
            <button
              type="button"
              className="ac-field-patch__btn ac-field-patch__btn--accept-idle"
              onClick={() => onDecision(diff.path, 'accepted')}
            >
              Accept
            </button>
            <button
              type="button"
              className="ac-field-patch__btn ac-field-patch__btn--reject-idle"
              onClick={() => onDecision(diff.path, 'rejected')}
            >
              Reject
            </button>
          </div>
        )}

        {accepted && (
          <div className="ac-field-patch__status ac-field-patch__status--accepted">
            <i className="fas fa-check-circle" aria-hidden="true" />
            <span>Accepted</span>
            {onDecision && (
              <button
                type="button"
                className="ac-field-patch__undo"
                onClick={() => onDecision(diff.path, 'pending')}
                title="Undo"
              >
                Undo
              </button>
            )}
          </div>
        )}

        {rejected && (
          <div className="ac-field-patch__status ac-field-patch__status--rejected">
            <i className="fas fa-times-circle" aria-hidden="true" />
            <span>Rejected</span>
            {onDecision && (
              <button
                type="button"
                className="ac-field-patch__undo"
                onClick={() => onDecision(diff.path, 'pending')}
                title="Undo"
              >
                Undo
              </button>
            )}
          </div>
        )}
      </div>

      {/* Diff block */}
      {pending && (
        <div className="ac-field-patch__diff">
          <NltTextFieldDiffLines
            oldValue={diff.oldValue}
            newValue={diff.newValue}
            mode="pending"
            variant="configurator"
          />
        </div>
      )}

      {accepted && (
        <div className="ac-field-patch__diff ac-field-patch__diff--accepted">
          <NltTextFieldDiffLines
            oldValue={diff.oldValue}
            newValue={diff.newValue}
            mode="accepted"
            variant="configurator"
          />
        </div>
      )}

      {rejected && (
        <div className="ac-field-patch__diff ac-field-patch__diff--rejected">
          <NltTextFieldDiffLines
            oldValue={diff.oldValue}
            newValue={diff.newValue}
            mode="rejected"
            variant="configurator"
            mutedRemoved
          />
        </div>
      )}

      {/* Field — hidden while pending to avoid duplicate content overlapping adjacent diffs */}
      {!pending && (
        <div
          className={[
            'ac-field-patch__field',
            rejected ? 'ac-field-patch__field--dimmed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default AcFieldPatchDiff;
